import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { GoldButton } from './GoldButton';
import { scrollToId } from '../../lib/smooth';
import { trackClick } from '../../lib/track';

const MaskedLine = ({ children, delay, innerClass = '' }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block will-change-transform ${innerClass}`}
      initial={{ y: '115%' }}
      animate={{ y: '0%' }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-[100svh] items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <motion.img
          src="/assets/hero-eagle.webp"
          alt="Águia-careca sob luz dourada"
          fetchPriority="high"
          className="h-[115%] w-full object-cover object-[70%_center] opacity-90"
          initial={{ scale: 1.18 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#030303] via-[#030303]/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-transparent to-[#030303]/70" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-6xl px-6 pt-24 pb-20 sm:px-10">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mb-8 flex items-center gap-3 font-accent text-[11px] tracking-[0.4em] uppercase text-gold"
        >
          <span className="h-px w-10 bg-gold/60" />
          Disciplina · Foco · Hábitos
        </motion.p>

        <h1 className="font-display text-[15vw] font-extrabold uppercase leading-[0.92] tracking-tight text-white sm:text-7xl lg:text-8xl">
          <MaskedLine delay={0.35}>Despertar</MaskedLine>
          <MaskedLine delay={0.5} innerClass="text-gold">da Águia</MaskedLine>
        </h1>

        <div className="mt-9 max-w-xl space-y-1.5 text-base leading-relaxed text-neutral-400 sm:text-lg">
          {[
            'Disciplina não é um dom. É uma decisão diária.',
            '28 dias para reconstruir seus hábitos e sua mentalidade.',
            'A águia não nasce no topo. Ela decide subir.',
          ].map((line, i) => (
            <motion.p
              key={line}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.95 + i * 0.15 }}
            >
              {line}
            </motion.p>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.5 }}
          className="mt-11 flex flex-col items-start gap-5 sm:flex-row sm:items-center"
        >
          <GoldButton
            testId="hero-cta-button"
            onClick={() => {
              trackClick('hero-baixar-app', 'aplicativo');
              scrollToId('#precos');
            }}
          >
            Baixar o app gratuitamente
          </GoldButton>
          <span className="font-accent text-[11px] tracking-[0.25em] uppercase text-neutral-500">
            Acesso imediato
          </span>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        className="absolute bottom-7 left-1/2 z-10 -translate-x-1/2 text-gold/60"
      >
        <ChevronDown className="h-5 w-5 animate-bounce" />
      </motion.div>
    </section>
  );
};
